import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import ChickenWalking from '../../assets/chickenwalking.gif';
import OrderSuccess from '../../assets/ordersuccess.gif';
import Button from '../components/Button';
import PageContainer from '../layouts/PageContainer';
import { trackV2Purchase } from '../services/tracking';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';

const CONFIRM_MS = 3000;

function lineRowTotal(line) {
  const q = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  return unit * q;
}

/** Same chicken + success flow as legacy `src/pages/OrderStatus.js` */
export default function OrderStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobile, user } = useV2Session();
  const orderId = location.state?.orderId;
  const receipt = location.state?.receipt ?? null;
  const [showConfirming, setShowConfirming] = React.useState(true);

  const purchaseTotal = receipt?.total;

  React.useEffect(() => {
    if (!orderId || purchaseTotal == null) return;
    trackV2Purchase({
      orderId,
      receipt,
      session: { mobile, user },
    });
  }, [orderId, purchaseTotal, receipt, mobile, user]);

  React.useEffect(() => {
    if (!orderId) return undefined;
    const t = window.setTimeout(() => setShowConfirming(false), CONFIRM_MS);
    return () => window.clearTimeout(t);
  }, [orderId]);

  if (!orderId) {
    return <Navigate to={paths.home} replace />;
  }

  const hasReceipt =
    receipt &&
    Array.isArray(receipt.products) &&
    receipt.products.length > 0 &&
    receipt.total != null;

  return (
    <PageContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          minHeight: showConfirming ? '55vh' : undefined,
          pt: showConfirming ? '10vh' : { xs: 2, sm: 4 },
        }}
      >
        {showConfirming ? (
          <>
            <Box
              component="img"
              src={ChickenWalking}
              alt="Confirming order"
              sx={{ width: { xs: 280, sm: 350 }, height: { xs: 280, sm: 350 }, objectFit: 'contain', alignSelf: 'center' }}
            />
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 4, fontSize: { xs: 18, sm: 20 } }}>
              Confirming order.. Please wait
            </Typography>
          </>
        ) : (
          <>
            <Box
              component="img"
              src={OrderSuccess}
              alt="Order confirmed"
              sx={{ width: 200, height: 200, objectFit: 'contain' }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mt: 1,
                mb: 2,
                textAlign: 'center',
                fontWeight: 800,
                color: '#a4243d',
                letterSpacing: 0.2,
              }}
            >
              Order placed successfully
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: 500,
                bgcolor: '#FFF0D9',
                borderRadius: '13px',
                boxShadow: '0px 0px 10px 0px #eaeaea',
                p: 2.5,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ width: '40%' }}>
                  Order ID
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: '#a4243d', textAlign: 'right' }}>
                  {orderId}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ width: '40%' }}>
                  Status
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  Pending
                </Typography>
              </Box>
              {receipt?.deliveryDate ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Typography variant="body1" sx={{ width: '40%' }}>
                    Delivery date
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'right' }}>
                    {String(receipt.deliveryDate)}
                  </Typography>
                </Box>
              ) : null}
              {receipt?.timeSlotLabel ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Typography variant="body1" sx={{ width: '40%' }}>
                    Slot
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'right' }}>
                    {receipt.timeSlotLabel}
                  </Typography>
                </Box>
              ) : null}
              {receipt?.paymentLabel ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body1" sx={{ width: '40%' }}>
                    Payment
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'right' }}>
                    {receipt.paymentLabel}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {hasReceipt ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Order items
                </Typography>
                <Box sx={{ maxHeight: 220, overflow: 'auto', pr: 0.5 }}>
                  {receipt.products.map((line, idx) => (
                    <Box
                      key={`${line.productId ?? idx}-${idx}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'flex-start',
                        py: 0.75,
                        borderBottom: idx < receipt.products.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {line.name || 'Item'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty {Number(line.quantity) || 0}
                          {line.variant?.weight ? ` · ${line.variant.weight}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                        ₹{lineRowTotal(line)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">
                    Items subtotal
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    ₹{Number(receipt.subtotal) || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    ₹{Number(receipt.shippingCost) || 0}
                  </Typography>
                </Box>
                {Number(receipt.discount) > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">
                      {receipt.appliedCoupon ? `Coupon (${receipt.appliedCoupon})` : 'Discount'}
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      −₹{Number(receipt.discount) || 0}
                    </Typography>
                  </Box>
                ) : null}
                {Number(receipt.loyaltyPoints) > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">
                      Naatu points
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      −₹{Number(receipt.loyaltyPoints) || 0}
                    </Typography>
                  </Box>
                ) : null}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="subtitle1" fontWeight={800}>
                    Order total
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="#a4243d">
                    ₹{Number(receipt.total) || 0}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 360 }}>
              <Button variant="outlined" fullWidth onClick={() => navigate(paths.account)} sx={{ borderRadius: '5px' }}>
                Account
              </Button>
              <Button variant="contained" fullWidth onClick={() => navigate(paths.home)} sx={{ borderRadius: '5px' }}>
                Continue shopping
              </Button>
            </Box>
          </>
        )}
      </Box>
    </PageContainer>
  );
}
